import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  IconButton,
} from '@mui/material';
import {
  Email as EmailIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

interface IMAPSetupProps {
  open: boolean;
  onClose: () => void;
  onComplete: (config: any) => void;
  initialData?: {
    fromEmail?: string;
    replyEmail?: string;
    imapHost?: string;
    imapPort?: number;
    imapPass?: string;
  };
}

const IMAPSetup: React.FC<IMAPSetupProps> = ({
  open,
  onClose,
  onComplete,
  initialData
}) => {
  const [imapConfig, setImapConfig] = useState({
    fromEmail: initialData?.fromEmail || '',
    replyEmail: initialData?.replyEmail || '',
    imapHost: initialData?.imapHost || '',
    imapPort: initialData?.imapPort || 993,
    imapPass: initialData?.imapPass || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Validate IMAP configuration
    if (!imapConfig.fromEmail.trim()) {
      toast.error('Please enter from email address');
      return;
    }

    if (!imapConfig.replyEmail.trim()) {
      toast.error('Please enter reply-to email address');
      return;
    }

    if (!imapConfig.imapHost.trim()) {
      toast.error('Please enter IMAP server host');
      return;
    }

    if (!imapConfig.imapPort || imapConfig.imapPort < 1 || imapConfig.imapPort > 65535) {
      toast.error('Please enter a valid IMAP port (1-65535)');
      return;
    }

    if (!imapConfig.imapPass.trim()) {
      toast.error('Please enter IMAP password');
      return;
    }

    // Basic validation for common IMAP hosts
    const commonHosts = ['imap.gmail.com', 'imap.outlook.com', 'imap.mail.yahoo.com'];
    const isValidHost = commonHosts.includes(imapConfig.imapHost.toLowerCase()) || 
                       imapConfig.imapHost.includes('.') && imapConfig.imapHost.length > 5;

    if (!isValidHost) {
      toast.error('Please enter a valid IMAP server host');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const config = {
        emailMethod: 'imap',
        fromEmail: imapConfig.fromEmail,
        replyEmail: imapConfig.replyEmail,
        imapHost: imapConfig.imapHost,
        imapPort: imapConfig.imapPort,
        imapPass: imapConfig.imapPass,
      };

      onComplete(config);
      toast.success('IMAP configuration saved successfully!');
    } catch (error) {
      toast.error('Failed to save IMAP configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        IMAP Email Setup
        <IconButton onClick={handleClose} size="small" disabled={isSubmitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure your IMAP server settings for email processing.
        </Typography>

        <Card>
          <CardHeader
            avatar={<EmailIcon />}
            title="IMAP Server Configuration"
          />
          <CardContent>
            <TextField
              fullWidth
              label="From Email Address"
              value={imapConfig.fromEmail}
              onChange={(e) => setImapConfig({ ...imapConfig, fromEmail: e.target.value })}
              placeholder="support@yourcompany.com"
              sx={{ mb: 2 }}
              required
              type="email"
            />
            
            <TextField
              fullWidth
              label="Reply-To Email Address"
              value={imapConfig.replyEmail}
              onChange={(e) => setImapConfig({ ...imapConfig, replyEmail: e.target.value })}
              placeholder="support@yourcompany.com"
              sx={{ mb: 2 }}
              required
              type="email"
            />
            
            <TextField
              fullWidth
              label="IMAP Server Host"
              value={imapConfig.imapHost}
              onChange={(e) => setImapConfig({ ...imapConfig, imapHost: e.target.value })}
              placeholder="imap.gmail.com"
              sx={{ mb: 2 }}
              required
              helperText="Common hosts: imap.gmail.com, imap.outlook.com, imap.mail.yahoo.com"
            />
            
            <TextField
              fullWidth
              type="number"
              label="IMAP Port"
              value={imapConfig.imapPort}
              onChange={(e) => setImapConfig({ ...imapConfig, imapPort: parseInt(e.target.value) || 993 })}
              placeholder="993"
              sx={{ mb: 2 }}
              required
              helperText="Common ports: 993 (SSL), 143 (TLS), 587 (SMTP)"
              inputProps={{ min: 1, max: 65535 }}
            />
            
            <TextField
              fullWidth
              type="password"
              label="Email Password"
              value={imapConfig.imapPass}
              onChange={(e) => setImapConfig({ ...imapConfig, imapPass: e.target.value })}
              placeholder="Your email password or app password"
              sx={{ mb: 2 }}
              required
            />
            
            <Alert severity="info">
              <AlertTitle>Important Notes:</AlertTitle>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <li>For Gmail, use an App Password instead of your regular password</li>
                <li>Make sure IMAP is enabled in your email account settings</li>
                <li>Use SSL/TLS encryption for secure connections</li>
              </Box>
            </Alert>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save IMAP Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IMAPSetup;
